import React, { FC, useEffect } from 'react';
import { OtpErrorResponse, ParsedPromo, PromoTypes } from '../types';
import { PromotionMessagesPrompt } from '../PromotionMessagesPrompt';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { checkIsUrlExternal } from '../../../../utils/links-helper/links-helper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { compareWithPromoType, isActivePromotion } from '../utils';
import { optInMxPromotion } from '../services';
import { ERROR_STR } from '../../../../consts';
import { useDispatch } from 'react-redux';
import { setLoadPlatformPromotions } from '../../../../state/promotions/reducers';
import { useSignIn } from '../../../../hooks/auth';
import { useSnackbar } from '../../../../hooks/snackbar';

export const PromotionClaim: FC<{
  promo: ParsedPromo | null;
  setShowClaim: (arg: boolean) => void;
}> = ({ promo, setShowClaim }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, signinRedirect } = useAuth();
  const signIn = useSignIn();
  const { addSnack } = useSnackbar();

  const showMessage = compareWithPromoType(promo?.promoType, PromoTypes.DEPOSIT_MATCH);

  const isActivePromotions = isActivePromotion(promo?.status);

  const goToCtaLink = (): void => {
    const ctaUrl = String(promo?.ctaUrl);

    if (checkIsUrlExternal(ctaUrl)) {
      window.location.href = String(ctaUrl);
    } else {
      navigate(`/${ctaUrl}`);
    }
  };

  const onClosePromptHandler = (): void => setShowClaim(false);

  useEffect(() => {
    if (isAuthenticated) {
      isActivePromotions && goToCtaLink();
    } else {
      signinRedirect({ redirect_uri: window.location.href });
    }
  }, []);

  const onPromotionClaimed = (): void => {
    addSnack({
      theme: 'dark',
      type: 'success',
      message: t('promotions.claimed'),
    });
  };

  const onClaimedError = (): void => {
    addSnack({
      theme: 'dark',
      type: 'error',
      message: t('error.common'),
      isDismissible: false,
    });
  };

  const onActivateBonus = async (): Promise<void> => {
    if (isAuthenticated) {
      const { magicXSlug, magicXCta } = promo || {};

      try {
        const optResponse = await optInMxPromotion(magicXSlug, magicXCta);

        if ((optResponse as OtpErrorResponse)?.name === ERROR_STR) {
          onClaimedError();
        } else {
          onPromotionClaimed();
        }

        dispatch(setLoadPlatformPromotions(true));
      } catch (error) {
        onClaimedError();
      } finally {
        setShowClaim(false);
      }
    } else {
      signIn();
    }
  };

  useEffect(() => {
    !(showMessage || isActivePromotions) && onActivateBonus();
  }, [showMessage]);

  return <>{showMessage && <PromotionMessagesPrompt onClose={onClosePromptHandler} activatedPromo={promo} />}</>;
};
